// Maps a notification's `screen` + `entity_type` + `entity_id` to a web route.
//
// The backend names a *subject* — `Fees`, `Attendance` — not a route, because
// the same subject is a different screen on each surface and for each role. See
// `ScreenNameTrait` in the API and `NotificationScreenRouter` in the mobile app;
// the three move together.
//
// `entity_type` is what decides whether a subject can be opened at a specific
// record: an attendance notification carries a Batch on one class and a
// StudentDetail on another, and the two are different pages here. Where the
// type is not one we can place, the CTA falls back to the list — better than a
// deep link built from an id of the wrong kind, which is what the old
// `/admin/fees/{id}` produced (it never matched a route at all: the fee page is
// `/admin/fees/:type/:feeId`).
//
// The console serves three roles — admin, manager and trainer — from the same
// `/admin/...` URL tree (role only changes the *API* base, see api/scope.ts).
// But some pages are role-gated and bounce a disallowed role back to the
// dashboard, so the CTA takes the role and simply does not offer a button that
// would go nowhere. Students have no web UI at all.
//
// Returns null when the subject has no web equivalent for this role, in which
// case the notification card renders without a CTA.

import type { Profile } from '@/api/types/common';

export interface NotificationCta {
  href: string;
  label: string;
}

/** The tail of a polymorphic `entity_type`, e.g. `App\Models\Batch` → `Batch`. */
function modelOf(entityType?: string): string {
  if (!entityType) return '';
  const parts = entityType.split('\\');
  return parts[parts.length - 1] ?? '';
}

/**
 * The target ids the API ships alongside `screen`.
 *
 * `entity_type` says what a notification is *about*; these say what to open.
 * They are not the same question — a payment-received notification is about a
 * Payment, but the page it wants is the invoice that payment settled. Every
 * key is optional: a notification sent before these existed simply has none,
 * and the CTA falls back to the same list it always did.
 *
 * Keep in step with `NotificationTargetTrait` in the API and
 * `NotificationTarget` in the mobile app.
 */
export interface NotificationTarget {
  batch_id?: number | string;
  conducted_class_id?: number | string;
  class_date?: string;
  class_start_time?: string;
  student_detail_id?: number | string;
  invoice_id?: number | string;
  invoice_kind?: string;
  lead_id?: number | string;
  trainer_detail_id?: number | string;
}

/** Empty string for anything absent, so callers can test with a plain falsy. */
function idOf(value?: number | string): string {
  return value == null || value === '' ? '' : String(value);
}

export function notificationCta(
  screen?: string,
  entityId?: number | string,
  entityType?: string,
  role?: Profile['role'],
  target: NotificationTarget = {},
): NotificationCta | null {
  if (!screen) return null;
  const id = entityId == null || entityId === '' ? '' : String(entityId);
  const model = modelOf(entityType);

  switch (screen) {
    // ---- leads
    case 'Leads': {
      // `lead_id` is what the API sends now, and it is the only reliable
      // source: a follow-up notification's entity_type is the FollowUp, which
      // has no page of its own, so the entity form below misses those.
      const leadId = idOf(target.lead_id);
      if (leadId) return { href: `/admin/leads/${leadId}`, label: 'View lead' };
      return model === 'Lead' && id
        ? { href: `/admin/leads/${id}`, label: 'View lead' }
        : { href: '/admin/leads', label: 'Open leads' };
    }

    // ---- attendance
    case 'Attendance':
    // Role-prefixed spellings the API sent before the 2026-08 audit. Rows
    // already in the notifications table still carry them.
    case 'AdminAttendance':
    case 'StudentAttendance':
      {
        // One class rather than the batch's month. BatchAttendance already
        // takes these: `class` opens a conducted class for editing, and
        // `take` + `slot` open the take-attendance sheet for a specific day
        // and start time. Which of the two we send is decided by whether a
        // conducted class exists — "not taken" is exactly the case where it
        // does not.
        const batchId = idOf(target.batch_id);
        const conductedId = idOf(target.conducted_class_id);
        if (batchId && conductedId) {
          return {
            href: `/admin/attendance/${batchId}?class=${conductedId}`,
            label: 'View attendance',
          };
        }
        if (batchId && target.class_date) {
          const slot = target.class_start_time
            ? `&slot=${encodeURIComponent(target.class_start_time)}`
            : '';
          return {
            href: `/admin/attendance/${batchId}?take=${target.class_date}${slot}`,
            label: 'Take attendance',
          };
        }
        if (batchId) {
          return {
            href: `/admin/attendance/${batchId}`,
            label: 'View attendance',
          };
        }
      }
      if (model === 'Batch' && id) {
        return { href: `/admin/attendance/${id}`, label: 'View attendance' };
      }
      if (model === 'StudentDetail' && id) {
        return { href: `/admin/students/${id}`, label: 'View student' };
      }
      return { href: '/admin/attendance', label: 'Open attendance' };

    // ---- fees
    case 'Fees':
    case 'AdminFee':
    case 'StudentFee':
    case 'Payment':
    case 'additional_fee_detail':
      {
        // The invoice the notification is about, which `entity_type` cannot
        // always give us: a payment-received notification's entity is the
        // payment, not the invoice it settles.
        const invoiceId = idOf(target.invoice_id);
        const kind = target.invoice_kind;
        if (invoiceId && (kind === 'batch' || kind === 'admission' || kind === 'additional')) {
          return { href: `/admin/fees/${kind}/${invoiceId}`, label: 'View fee' };
        }
      }
      if (id) {
        if (model === 'BatchFeeInvoice') {
          return { href: `/admin/fees/batch/${id}`, label: 'View fee' };
        }
        if (model === 'AdmissionFeeInvoice') {
          return { href: `/admin/fees/admission/${id}`, label: 'View fee' };
        }
        if (model === 'AdditionalFeeInvoice') {
          return { href: `/admin/fees/additional/${id}`, label: 'View fee' };
        }
        if (model === 'StudentDetail') {
          return { href: `/admin/students/${id}/fees`, label: 'View fees' };
        }
      }
      return { href: '/admin/fees', label: 'Open fees' };

    // ---- people
    case 'Students':
    case 'StudentProfile': {
      // A rejoining notification's entity is the Batch, not the student — the
      // student is what it is about, and student_detail_id is how we get it.
      const studentId = idOf(target.student_detail_id);
      if (studentId) {
        return { href: `/admin/students/${studentId}`, label: 'View student' };
      }
      return model === 'StudentDetail' && id
        ? { href: `/admin/students/${id}`, label: 'View student' }
        : { href: '/admin/students', label: 'Open students' };
    }

    case 'Trainers': {
      // `/admin/trainers` is admin + manager only.
      if (role === 'trainer') return null;
      const trainerId = idOf(target.trainer_detail_id);
      if (trainerId) {
        return { href: `/admin/trainers/${trainerId}`, label: 'View trainer' };
      }
      return model === 'TrainerDetail' && id
        ? { href: `/admin/trainers/${id}`, label: 'View trainer' }
        : { href: '/admin/trainers', label: 'Open trainers' };
    }

    // ---- classes
    case 'TodaysClasses': {
      // A cancelled or rescheduled class is about one batch on one day, and
      // the batch's attendance page is the only screen that opens on a date.
      // Today's Classes only ever shows today, so it is wrong for a class
      // that moved to next week.
      const batchId = idOf(target.batch_id);
      if (batchId && target.class_date) {
        return {
          href: `/admin/attendance/${batchId}?take=${target.class_date}`,
          label: 'View class',
        };
      }
      return { href: '/admin/todays-classes', label: "Today's classes" };
    }

    case 'Batches':
      return model === 'Batch' && id
        ? { href: `/admin/batches/${id}`, label: 'View batch' }
        : { href: '/admin/batches', label: 'Open batches' };

    // The console has no class-link page; batches is where a link is set.
    case 'ClassLinks': {
      // The link is added against a batch, but entity_type is the ClassLink,
      // which has no page — so the batch never resolved from it.
      const batchId = idOf(target.batch_id);
      if (batchId) return { href: `/admin/batches/${batchId}`, label: 'View batch' };
      return model === 'Batch' && id
        ? { href: `/admin/batches/${id}`, label: 'View batch' }
        : { href: '/admin/batches', label: 'Open batches' };
    }

    case 'Salary':
      return { href: '/admin/salary', label: 'Open salary' };

    // ---- membership. Never renamed: older mobile builds route on these exact
    // strings, so the API cannot change them.
    case 'Membership':
    case 'MembershipHome':
    case 'Subscription':
    case 'SubscriptionPayment':
    case 'SetupMandate':
      // The membership page is admin-only. These notifications only go to
      // admins anyway, but a manager or trainer reading one should not be
      // handed a button that bounces them to the dashboard.
      return role && role !== 'admin'
        ? null
        : { href: '/admin/membership', label: 'Open membership' };

    // 'Notification' and anything unrecognised: the card is already on the
    // notifications page, so there is nowhere to send the reader.
    default:
      return null;
  }
}
